import { createParser } from 'eventsource-parser';
import { getAppPath, isCurrentAppPath } from './appPath';
import retryDelayWithJitter, { waitForRetry } from '../../../utils/retryDelay';

const isSuccess = response =>
  response && response.status >= 200 && response.status < 300;

const getError = response => {
  let errors = [{ message: 'An unknown error occurred' }];
  if (response?.status >= 500) {
    errors = [
      {
        errorCode: 'SERVER_ERROR',
        message:
          "We're unable to complete your request right now. Please try again later or contact support if the issue persists.",
      },
    ];
  } else if (response?.data?.errors && Array.isArray(response?.data?.errors)) {
    errors = response.data.errors;
  } else if (response && response.data) {
    errors = [response.data];
  }
  return { errors, ok: false, data: null, statusCode: response?.status };
};

const getBlobError = async response => {
  const errorText = await response.data.text();
  const errorJson = JSON.parse(errorText);
  return getError({
    data: { errors: [errorJson] },
    status: response.status,
  });
};

const getSuccess = response => ({
  data: response.data,
  errors: [],
  ok: true,
});

const resolveRequestConfig = async (axiosWrapper, config) => {
  const handlers = axiosWrapper.interceptors.request.handlers || [];
  return handlers.reduce(
    (promise, handler) =>
      promise.then(current =>
        typeof handler?.fulfilled === 'function'
          ? Promise.resolve(handler.fulfilled(current))
          : current,
      ),
    Promise.resolve(config),
  );
};

const buildFetchUrl = (axiosWrapper, resource) => {
  const baseURL = axiosWrapper.defaults.baseURL || '';
  const path = resource.startsWith('/') ? resource.slice(1) : resource;
  return `${baseURL.replace(/\/$/, '')}/${path}`;
};

const dataProvider = axiosWrapper => ({
  getApiUrl: () => axiosWrapper.defaults.baseURL,
  getMany: async resource => {
    try {
      const response = await axiosWrapper.get(resource);
      return response?.data;
    } catch (error) {
      if (error?.response?.data) {
        return error?.response?.data;
      }
      return { error: [{ message: error.message }] };
    }
  },
  getOne: async (resource, data = null, config = null) => {
    try {
      const response = await axiosWrapper.get(resource, {
        ...(data && { params: data }),
        ...config,
      });
      return response.data;
    } catch (error) {
      if (error?.response?.data) {
        return error?.response?.data;
      }
      return { error: [{ message: error.message }] };
    }
  },
  deleteOne: async (resource, data) => {
    try {
      const response = await axiosWrapper.delete(resource, data);
      return response.data;
    } catch (error) {
      if (error?.response?.data) {
        return error?.response?.data;
      }
      return { error: [{ message: error.message }] };
    }
  },
  createOne: async (resource, data) => {
    try {
      const response = await axiosWrapper.put(resource, data);
      return response.data;
    } catch (error) {
      if (error?.response?.data) {
        return error?.response?.data;
      }
      return { error: [{ message: error.message }] };
    }
  },
  updateOne: async (resource, data) => {
    try {
      const response = await axiosWrapper.post(resource, data);
      return response.data;
    } catch (error) {
      if (error?.response?.data) {
        return error?.response?.data;
      }
      return { error: [{ message: error.message }] };
    }
  },
  postApi: async (resource, data) => {
    try {
      const response = await axiosWrapper.post(resource, data);
      return response.data;
    } catch (error) {
      if (error?.response?.data) {
        return error?.response?.data;
      }
      return { error: [{ message: error.message }] };
    }
  },
  getApi: async (resource, data = null, config = null) => {
    try {
      const response = await axiosWrapper.get(resource, {
        ...(data && { params: data }),
        ...config,
      });
      return response.data;
    } catch (error) {
      if (error?.response?.data) {
        return error?.response?.data;
      }
      return { error: [{ message: error.message }] };
    }
  },
  putApi: async (resource, data) => {
    try {
      const response = await axiosWrapper.put(resource, data);
      return response.data;
    } catch (error) {
      if (error?.response?.data) {
        return error?.response?.data;
      }
      return { error: [{ message: error.message }] };
    }
  },
  get: async (resource, data = null, config = null) => {
    try {
      const response = await axiosWrapper.get(resource, {
        ...(data && { params: data }),
        ...config,
      });
      if (isSuccess(response)) {
        return getSuccess(response);
      }
      return getError(response);
    } catch (error) {
      return getError(error?.response);
    }
  },
  getBlob: async (resource, data = null, config = null) => {
    try {
      const response = await axiosWrapper.get(resource, {
        ...(data && { params: data }),
        ...config,
        responseType: 'blob',
      });
      if (isSuccess(response)) {
        return {
          ...response,
          ok: true,
        };
      }
      return getBlobError(response);
    } catch (error) {
      if (error?.response?.data instanceof Blob) {
        return getBlobError(error.response);
      }
      return getError(error?.response);
    }
  },
  post: async (resource, data, config) => {
    try {
      const response = await axiosWrapper.post(resource, data, config);

      if (isSuccess(response)) {
        return getSuccess(response);
      }
      return getError(response);
    } catch (error) {
      return getError(error?.response);
    }
  },
  put: async (resource, data) => {
    try {
      const response = await axiosWrapper.put(resource, data);
      if (isSuccess(response)) {
        return getSuccess(response);
      }
      return getError(response);
    } catch (error) {
      return getError(error?.response);
    }
  },
  patch: async (resource, data) => {
    try {
      const response = await axiosWrapper.patch(resource, data);
      if (isSuccess(response)) {
        return getSuccess(response);
      }
      return getError(response);
    } catch (error) {
      return getError(error?.response);
    }
  },
  remove: async (resource, data) => {
    try {
      const response = await axiosWrapper.delete(resource, data);
      if (isSuccess(response)) {
        return getSuccess(response);
      }
      return getError(response);
    } catch (error) {
      return getError(error?.response);
    }
  },
  readStreamResult: async url => {
    const config = {
      method: 'get',
      headers: {
        ...axiosWrapper.defaults.headers?.common,
      },
    };
    const resolvedConfig = await resolveRequestConfig(axiosWrapper, config);
    const response = await fetch(url, {
      method: 'GET',
      headers: resolvedConfig.headers,
    });

    if (!response.ok) {
      return getError({
        data: { message: response.statusText },
        status: response.status,
      });
    }

    if (!response.body) {
      return getError({
        data: { message: 'No response body stream available' },
        status: response.status,
      });
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    let buffer = '';
    let latestEvent = null;
    let latestData = null;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      // eslint-disable-next-line no-restricted-syntax
      for (const line of lines) {
        const trimmed = line.trim();

        if (!trimmed) {
          // eslint-disable-next-line no-continue
          continue;
        }

        if (trimmed.startsWith('event:')) {
          // eslint-disable-next-line no-unused-vars
          latestEvent = trimmed.slice(6).trim();
        }

        if (trimmed.startsWith('data:')) {
          const raw = trimmed.slice(5).trim();

          try {
            latestData = JSON.parse(raw);
          } catch {
            // ignore bad chunk
          }
        }
      }
    }

    if (!latestData) {
      throw new Error('Stream ended without receiving data payload');
    }

    return getSuccess({ ok: true, data: latestData, errors: [] });
  },
  stream: async (resource, data, { signal } = {}) => {
    const url = buildFetchUrl(axiosWrapper, resource);
    const config = {
      url: resource,
      method: 'post',
      data,
      headers: {
        ...axiosWrapper.defaults.headers?.common,
        'Content-Type': 'application/json',
      },
    };
    const resolvedConfig = await resolveRequestConfig(axiosWrapper, config);
    return fetch(url, {
      method: 'POST',
      headers: resolvedConfig.headers,
      body: JSON.stringify(data),
      signal,
    });
  },
  subscribe: async (
    resource,
    { signal, onMessage, onError, retryDelayMs = 1000 } = {},
  ) => {
    if (!signal) {
      throw new Error('A subscription requires an AbortSignal');
    }

    const url = buildFetchUrl(axiosWrapper, resource);
    let retryDelay = retryDelayMs;
    let retryAttempt = 0;
    let awaitingTokenRenewal = false;

    while (!signal.aborted) {
      try {
        // Resolve interceptors on every attempt so a renewed token is used.
        // eslint-disable-next-line no-await-in-loop
        const config = await resolveRequestConfig(axiosWrapper, {
          url: resource,
          method: 'get',
          headers: { ...axiosWrapper.defaults.headers?.common },
        });
        // eslint-disable-next-line no-await-in-loop
        const response = await fetch(url, {
          method: 'GET',
          headers: config.headers,
          credentials: axiosWrapper.defaults.withCredentials
            ? 'include'
            : 'same-origin',
          signal,
        });

        if (!response.ok) {
          if (response.status === 401) {
            if (signal.aborted || isCurrentAppPath('/unauthorized')) {
              return;
            }
            if (!awaitingTokenRenewal) {
              awaitingTokenRenewal = true;
              // Allow the existing OIDC silent renewal to update token storage.
              // eslint-disable-next-line no-await-in-loop
              await waitForRetry(5000, signal);
              // eslint-disable-next-line no-continue
              continue;
            }
            window.location.href = getAppPath('/unauthorized');
            return;
          }
          if (response.status === 403) {
            const error = new Error('Notification stream access denied');
            error.status = 403;
            onError?.(error);
            return;
          }
          throw new Error(`Notification stream returned ${response.status}`);
        }
        if (!response.body) {
          throw new Error('No response body stream available');
        }

        // The parser updates the server-selected delay for the next attempt.
        const parser = createParser({
          // eslint-disable-next-line no-loop-func
          onEvent: message => {
            retryAttempt = 0;
            awaitingTokenRenewal = false;
            onMessage?.(message);
          },
          // eslint-disable-next-line no-loop-func
          onRetry: delay => {
            retryDelay = delay;
          },
        });
        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        while (!signal.aborted) {
          // eslint-disable-next-line no-await-in-loop
          const { done, value } = await reader.read();
          if (done) {
            break;
          }
          parser.feed(decoder.decode(value, { stream: true }));
        }
      } catch (error) {
        if (signal.aborted || error?.name === 'AbortError') {
          return;
        }
        onError?.(error);
      }

      // eslint-disable-next-line no-await-in-loop
      await waitForRetry(
        retryDelayWithJitter(retryAttempt, retryDelay),
        signal,
      );
      retryAttempt += 1;
    }
  },
  custom: () => Promise.resolve({ data: null }),
});

export default dataProvider;
